import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  Divider,
  Chip,
  InputAdornment,
  Tooltip,
  Alert,
  useTheme,
  alpha,
  Card,
  CardContent,
  Stack,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Container,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  Payment as PaymentIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Warehouse as WarehouseIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Category as CategoryIcon,
  Scale as ScaleIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import {
  PurchaseInvoice,
  CreatePurchaseInvoiceDto,
  UpdatePurchaseInvoiceDto,
} from '../../types/purchaseInvoices.types';
import { purchaseInvoicesService } from '../../services/purchaseInvoices.service';
import suppliersService from '../../services/suppliers.service';
import { warehousesService } from '../../services/warehouses.service';
import { itemsService, itemUnitsService, unitsService } from '../../services/items.service';
import { itemSuppliersService } from '../../services/itemSuppliers.service';
import { Supplier } from '../../types/suppliers.types';
import { Warehouse } from '../../types/warehouses.types';
import { Item } from '../../types/items.types';
import { ItemBySupplier, ItemPolicy } from '../../types/itemSuppliers.types';
import { custodiesService } from '../../services/custodies.service';
import axiosInstance from '../../services/axiosInstance';
import { Custody } from '../../types/custodies.types';
import { chartOfAccountsService } from '../../services/chartOfAccounts.service';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const translations = {
  en: {
    title: 'Purchase Invoice',
    invoiceDetails: 'Invoice Details',
    items: 'Items',
    summary: 'Summary',
    invoiceNumber: 'Invoice #',
    invoiceDate: 'Date',
    supplier: 'Supplier',
    warehouse: 'Warehouse',
    custody: 'Custody',
    selectCustody: 'Select custody (optional)...',
    noCustody: 'No Custody',
    paymentAccount: 'Payment Account',
    selectPaymentAccount: 'Select payment account...',
    kwd: 'KWD',
    supplierInvoice: 'Supplier Invoice #',
    paymentType: 'Payment',
    dueDate: 'Due Date',
    paidAmount: 'Paid',
    item: 'Item',
    unit: 'Unit/Pkg',
    quantity: 'Qty',
    price: 'Price',
    discount: 'Disc %',
    tax: 'Tax %',
    total: 'Total',
    addItem: 'Add Item',
    headerDiscount: 'Invoice Discount %',
    headerTax: 'Invoice Tax %',
    shipping: 'Shipping',
    subtotal: 'Subtotal',
    grandTotal: 'Grand Total',
    paid: 'Paid',
    balance: 'Balance',
    save: 'Save Invoice',
    cancel: 'Cancel',
    notes: 'Notes',
    selectSupplier: 'Please select supplier first',
    selectItem: 'Select item...',
    selectUnit: 'Select unit...',
    itemsAvailable: 'items available',
    priceFromSupplier: 'Price locked by supplier',
    itemNotAvailable: 'This item is not available from selected supplier',
    supplierRequired: 'This item requires a supplier to be selected first',
    invoiceSaved: 'Invoice saved successfully!',
    invoiceSaveFailed: 'Failed to save invoice',
    // Item Policy Labels
    policySupplierOnly: 'Supplier Required',
    policyPriceOnly: 'Price Locked',
    policySupplierAndPrice: 'Supplier + Price Locked',
  },
  ar: {
    title: 'فاتورة شراء',
    invoiceDetails: 'بيانات الفاتورة',
    items: 'الأصناف',
    summary: 'الملخص',
    invoiceNumber: 'رقم الفاتورة',
    invoiceDate: 'التاريخ',
    supplier: 'المورد',
    warehouse: 'المخزن',
    custody: 'العهدة',
    selectCustody: 'اختر العهدة (اختياري)...',
    noCustody: 'بدون عهدة',
    paymentAccount: 'حساب الدفع',
    selectPaymentAccount: 'اختر حساب الدفع...',
    kwd: 'د.ك',
    supplierInvoice: 'رقم فاتورة المورد',
    paymentType: 'نوع الدفع',
    dueDate: 'تاريخ الاستحقاق',
    paidAmount: 'المدفوع',
    item: 'الصنف',
    unit: 'الوحدة/التعبئة',
    quantity: 'الكمية',
    price: 'السعر',
    discount: 'خصم %',
    tax: 'ضريبة %',
    total: 'الإجمالي',
    addItem: 'إضافة صنف',
    headerDiscount: 'خصم الفاتورة %',
    headerTax: 'ضريبة الفاتورة %',
    shipping: 'الشحن',
    subtotal: 'المجموع الفرعي',
    grandTotal: 'الإجمالي الكلي',
    paid: 'المدفوع',
    balance: 'المتبقي',
    save: 'حفظ الفاتورة',
    cancel: 'إلغاء',
    notes: 'ملاحظات',
    selectSupplier: 'اختر المورد أولاً',
    selectItem: 'اختر الصنف...',
    selectUnit: 'اختر الوحدة...',
    itemsAvailable: 'صنف متوفر',
    priceFromSupplier: 'سعر محدد من المورد',
    itemNotAvailable: 'هذا الصنف غير متوفر من المورد المختار',
    supplierRequired: 'هذا الصنف يتطلب اختيار المورد أولاً',
    invoiceSaved: 'تم حفظ الفاتورة بنجاح!',
    invoiceSaveFailed: 'فشل حفظ الفاتورة',
    // Item Policy Labels
    policySupplierOnly: 'مورد إجباري',
    policyPriceOnly: 'سعر مقفول',
    policySupplierAndPrice: 'مورد + سعر إجباري',
  },
};

interface PurchaseInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseInvoice?: PurchaseInvoice;
  onSave: () => void;
  fixedWarehouseId?: number;
  bakeryPurchaseOnly?: boolean;
}

interface Unit {
  unitId: number;
  unitName: string;
  unitNameAr: string;
  unitCode: string;
}

interface ItemUnit {
  unitId: number;
  unitName: string;
  unitNameAr: string;
  unitCode: string;
  conversionFactor: number;
  defaultPurchasePrice: number;
  defaultSalePrice: number;
  isDefaultPurchase: boolean;
  isDefaultSale: boolean;
}

interface DetailLine {
  id: string;
  itemId: number | null;
  item: Item | null;
  unitId: number | null;
  unit: ItemUnit | null;
  availableUnits: ItemUnit[];
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  taxPercent: number;
  batchNumber: string;
  expiryDate: string;
  notes: string;
  isPriceReadOnly?: boolean;
  itemPolicy?: ItemPolicy;
}

const PurchaseInvoiceDialog: React.FC<PurchaseInvoiceDialogProps> = ({
  open,
  onClose,
  purchaseInvoice,
  onSave,
  fixedWarehouseId,
  bakeryPurchaseOnly,
}) => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  
  const { minDate, maxDate } = useFiscalDateRange();
  const isUserSuperAdmin = useSelector((state: RootState) => state.auth.user?.isSuperAdmin) ?? false;
  const isEditMode = !!purchaseInvoice;
  const dateLocked = isEditMode && !isUserSuperAdmin;

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  // Function to get translated policy label
  const getPolicyLabel = (policy: ItemPolicy | undefined): string => {
    if (!policy) return '';
    
    const isSupplierReq = policy.isSupplierRequired;
    const isPriceCtrl = policy.isPriceControlled;

    if (isSupplierReq && isPriceCtrl) {
      return t.policySupplierAndPrice; // Supplier + Price Locked
    } else if (isSupplierReq) {
      return t.policySupplierOnly; // Supplier Required
    } else if (isPriceCtrl) {
      return t.policyPriceOnly; // Price Locked
    }
    return '';
  };

  const [activeStep, setActiveStep] = useState(0);
  const steps = [t.invoiceDetails, t.items, t.summary];

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    supplierId: null as number | null,
    warehouseId: null as number | null,
    custodyId: null as number | null,
    paymentAccountId: null as number | null,
    supplierInvoiceNumber: '',
    paymentType: 'Cash',
    dueDate: '',
    discountPercent: 0,
    taxPercent: 0,
    shippingCost: 0,
    paidAmount: 0,
    notes: '',
  });

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [supplierItems, setSupplierItems] = useState<ItemBySupplier[]>([]);

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [custodies, setCustodies] = useState<Custody[]>([]);
  const [selectedCustody, setSelectedCustody] = useState<Custody | null>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedPaymentAccount, setSelectedPaymentAccount] = useState<any>(null);
  const [isBranchManager, setIsBranchManager] = useState(false);
  const [isWarehouseKeeper, setIsWarehouseKeeper] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const [detailLines, setDetailLines] = useState<DetailLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadMasterData();
      setActiveStep(0);
    }
  }, [open]);

  useEffect(() => {
    if (open && suppliers.length > 0 && warehouses.length > 0 && items.length > 0) {
      if (purchaseInvoice) {
        loadPurchaseInvoice();
      } else {
        loadNewInvoiceNumber();
        addNewLine();
      }
    }
  }, [open, purchaseInvoice, suppliers, warehouses, items]);

  useEffect(() => {
    if (formData.supplierId) {
      loadSupplierItems(formData.supplierId);
      updatePricesForSupplier(formData.supplierId);
    } else {
      setSupplierItems([]);
    }
  }, [formData.supplierId]);

  const loadMasterData = async () => {
    try {
      const [suppliersData, warehousesData, itemsData, custodiesData, accountsData] = await Promise.all([
        suppliersService.getAll(),
        bakeryPurchaseOnly ? axiosInstance.get('/Warehouses/lookup').then(r => r.data) : warehousesService.getOperational(),
        bakeryPurchaseOnly ? itemsService.getBakeryPurchaseItems() : itemsService.getAllExcludingBakeryPurchase(),
        axiosInstance.get('/Custodies/purchase-lookup').then(res => res.data),
        chartOfAccountsService.getAll(),
      ]);
      setSuppliers(suppliersData);
    
      // Read user branches from JWT token
      const token = localStorage.getItem('token');
      let userBranchIds: number[] = [];
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
          const allowedBranches = payload.AllowedBranchId
            ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
            : [];
          userBranchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
        } catch (e) { console.error('Failed to parse token:', e); }
      }
      setIsBranchManager(userBranchIds.length > 0);
      try { const p = JSON.parse(atob((localStorage.getItem('token') || '').split('.')[1])); const roles = Array.isArray(p['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']) ? p['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] : [p['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']]; setIsSuperAdmin(roles.includes('Super Admin')); } catch { setIsSuperAdmin(false); }

      // Read keeper warehouse IDs from JWT token
      let userWarehouseIds: number[] = [];
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const keeperClaims = payload.KeeperWarehouseId;
          userWarehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
        } catch (e) { console.error('Failed to parse keeper warehouses:', e); }
      }
      setIsWarehouseKeeper(userWarehouseIds.length > 0);

      // Filter warehouses: bakery page shows all, otherwise filter by user access
      const filteredWarehouses = fixedWarehouseId
        ? warehousesData
        : userWarehouseIds.length > 0
          ? warehousesData.filter((w: Warehouse) => userWarehouseIds.includes(w.warehouseId))
          : userBranchIds.length === 0
            ? warehousesData
            : warehousesData.filter((w: Warehouse) =>
                w.branchId && userBranchIds.includes(w.branchId)
              );
      setWarehouses(filteredWarehouses);
    
      setItems(itemsData);

      // إذا كان المخزن ثابتاً (صفحة مشتريات المخبز) — حدده تلقائياً
      if (fixedWarehouseId) {
        const fixedWh = warehousesData.find((w: Warehouse) => w.warehouseId === fixedWarehouseId);
        if (fixedWh) {
          setSelectedWarehouse(fixedWh);
          setFormData(prev => ({ ...prev, warehouseId: fixedWarehouseId }));
          // Ensure fixed warehouse is in the list
          if (!filteredWarehouses.some((w: Warehouse) => w.warehouseId === fixedWarehouseId)) {
            setWarehouses([fixedWh, ...filteredWarehouses]);
          }
        }
      }

      const custList = Array.isArray(custodiesData) ? custodiesData : (custodiesData?.data || []);
      setCustodies(custList);
    
      // Filter to get final accounts only (leaf accounts that can be used in transactions)
      const leafAccounts = accountsData.filter(acc => acc.isFinalAccount && acc.isActive);
      setAccounts(leafAccounts);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  };

  const loadSupplierItems = async (supplierId: number) => {
    try {
      const items = await itemSuppliersService.getItemsBySupplier(supplierId);
      setSupplierItems(items);
    } catch (error) {
      console.error('Failed to load supplier items:', error);
      setSupplierItems([]);
    }
  };

  const loadPurchaseInvoice = () => {
    if (!purchaseInvoice) return;

    setFormData({
      invoiceNumber: purchaseInvoice.invoiceNumber,
      invoiceDate: purchaseInvoice.invoiceDate.split('T')[0],
      supplierId: purchaseInvoice.supplierId || null,
      warehouseId: purchaseInvoice.warehouseId,
      custodyId: purchaseInvoice.custodyId || null,
      paymentAccountId: purchaseInvoice.paymentAccountId || null,
      supplierInvoiceNumber: purchaseInvoice.supplierInvoiceNumber || '',
      paymentType: purchaseInvoice.paymentType || 'Cash',
      dueDate: purchaseInvoice.dueDate?.split('T')[0] || '',
      discountPercent: purchaseInvoice.discountPercent || 0,
      taxPercent: purchaseInvoice.taxPercent || 0,
      shippingCost: purchaseInvoice.shippingCost || 0,
      paidAmount: purchaseInvoice.paidAmount || 0,
      notes: purchaseInvoice.notes || '',
    });

    const supplier = suppliers.find(s => s.supplierId === purchaseInvoice.supplierId);
    const warehouse = warehouses.find(w => w.warehouseId === purchaseInvoice.warehouseId);

    setSelectedSupplier(supplier || null);
    setSelectedWarehouse(warehouse || null);
    
    const custody = custodies.find(c => c.custodyId === purchaseInvoice.custodyId);
    setSelectedCustody(custody || null);
    
    const paymentAccount = accounts.find(a => a.accountId === purchaseInvoice.paymentAccountId);
    setSelectedPaymentAccount(paymentAccount || null);

    // Load details with units from API
    const loadLinesAsync = async () => {
      const linesPromises = purchaseInvoice.details.map(async (detail) => {
        const item = items.find(i => i.itemId === detail.itemId);
        
        // Get units from API
        let itemUnits: any[] = [];
        let selectedUnit = null;
        
        try {
          itemUnits = await itemUnitsService.getItemUnits(detail.itemId);
          selectedUnit = itemUnits.find((u: any) => u.unitId === detail.unitId) || null;
        } catch (error) {
          console.error('Failed to load units:', error);
        }

        return {
          id: `${detail.invoiceDetailId}`,
          itemId: detail.itemId,
          item: item || null,
          unitId: detail.unitId,
          unit: selectedUnit,
          availableUnits: itemUnits,
          quantity: detail.quantity,
          unitPrice: detail.unitPrice,
          discountPercent: detail.discountPercent || 0,
          taxPercent: detail.taxPercent || 0,
          batchNumber: detail.batchNumber || '',
          expiryDate: detail.expiryDate?.split('T')[0] || '',
          notes: detail.notes || '',
        };
      });
      
      const lines = await Promise.all(linesPromises);
      setDetailLines(lines);
    };
    
    loadLinesAsync().catch(err => console.error('Failed to load invoice lines:', err));
  };

  const loadNewInvoiceNumber = async () => {
    try {
      const invoiceNumber = await purchaseInvoicesService.generateInvoiceNumber();
      setFormData((prev) => ({ ...prev, invoiceNumber }));
    } catch (error) {
      console.error('Failed to generate invoice number:', error);
    }
  };

  const addNewLine = () => {
    const newLine: DetailLine = {
      id: `temp-${Date.now()}`,
      itemId: null,
      item: null,
      unitId: null,
      unit: null,
      availableUnits: [],
      quantity: 1,
      unitPrice: 0,
      discountPercent: 0,
      taxPercent: 0,
      batchNumber: '',
      expiryDate: '',
      notes: '',
    };
    setDetailLines((prev) => [...prev, newLine]);
  };

  const removeLine = (id: string) => {
    setDetailLines((prev) => prev.filter((line) => line.id !== id));
  };

  const updatePricesForSupplier = async (supplierId: number) => {
    if (detailLines.length === 0) return;
    
    const updatedLines = await Promise.all(
      detailLines.map(async (line) => {
        if (!line.itemId || !line.unitId) return line;

        try {
          const priceInfo = await itemSuppliersService.getPrice(
            line.itemId,
            supplierId,
            line.unitId
          );

          if (priceInfo && line.itemPolicy) {
            return {
              ...line,
              unitPrice: priceInfo.price,
              isPriceReadOnly: line.itemPolicy.isPriceControlled,
            };
          }
        } catch (error) {
          // Silently fail - price not found is OK
        }
        return line;
      })
    );
    setDetailLines(updatedLines);
  };

  const handleItemChange = async (lineId: string, item: Item | null) => {
    if (!item) {
      updateLine(lineId, 'item', null);
      updateLine(lineId, 'itemId', null);
      updateLine(lineId, 'availableUnits', []);
      updateLine(lineId, 'unit', null);
      updateLine(lineId, 'unitId', null);
      return;
    }

    try {
      const policy = await itemSuppliersService.getItemPolicy(item.itemId);
      
      if (policy.isSupplierRequired && !formData.supplierId) {
        toast.warning(t.supplierRequired);
        return;
      }

      // Check approved suppliers for this item
      if (formData.supplierId) {
        const approvedSuppliers = await itemSuppliersService.getSuppliersByItem(item.itemId, true);
        
        if (approvedSuppliers.length > 0) {
          const isApproved = approvedSuppliers.some(s => s.supplierId === formData.supplierId);
          
          if (!isApproved) {
            const supplierNames = approvedSuppliers.map(s => 
              language === 'ar' ? s.supplierNameAr : s.supplierName
            ).join(', ');
            
            toast.error(
              language === 'ar' 
                ? `هذا الصنف متاح فقط من الموردين: ${supplierNames}`
                : `This item is only available from suppliers: ${supplierNames}`
            );
            return;
          }
        }
      }

      // Get units using API instead of item.itemUnits
      let units = await itemUnitsService.getItemUnits(item.itemId);
      
      // Check if base unit exists in ItemUnits
      const hasBaseUnit = units.some(u => u.unitId === item.baseUnitId);
      
      if (!hasBaseUnit && item.baseUnitId) {
        // Base unit missing - add it manually
        const allUnits = await unitsService.getAll();
        const baseUnit = allUnits.find(u => u.unitId === item.baseUnitId);
        
        if (baseUnit) {
          const baseUnitAsItemUnit = {
            itemUnitId: 0,
            itemId: item.itemId,
            unitId: baseUnit.unitId,
            unitName: baseUnit.unitName,
            unitNameAr: baseUnit.unitNameAr,
            unitCode: baseUnit.unitCode,
            conversionFactor: 1,
            defaultSalePrice: 0,
            defaultPurchasePrice: 0,
            isDefaultSale: true,
            isDefaultPurchase: true,
          };
          units = [baseUnitAsItemUnit, ...units];
        }
      }

      // Select default unit (Priority: baseUnit > defaultPurchase > first)
      let defaultUnit = units.find(u => u.unitId === item.baseUnitId);
      if (!defaultUnit) {
        defaultUnit = units.find(u => u.isDefaultPurchase);
      }
      if (!defaultUnit) {
        defaultUnit = units[0];
      }

      let unitPrice = 0;
      let isPriceReadOnly = false;

      if (formData.supplierId && defaultUnit) {
        const priceInfo = await itemSuppliersService.getPrice(
          item.itemId,
          formData.supplierId,
          defaultUnit.unitId
        );

        if (priceInfo) {
          unitPrice = priceInfo.price;
          isPriceReadOnly = policy.isPriceControlled;
        } else if (policy.isSupplierRequired) {
          toast.error(t.itemNotAvailable);
          return;
        } else if (defaultUnit) {
          const itemUnit = units.find(u => u.unitId === defaultUnit!.unitId);
          if (itemUnit) {
            unitPrice = itemUnit.defaultPurchasePrice || 0;
          }
        }
      } else if (defaultUnit) {
        // No supplier (custody purchase) — use default purchase price
        const itemUnit = units.find(u => u.unitId === defaultUnit!.unitId);
        if (itemUnit) {
          unitPrice = itemUnit.defaultPurchasePrice || 0;
        }
      }

      setDetailLines((prev) =>
        prev.map((line) =>
          line.id === lineId
            ? {
                ...line,
                item,
                itemId: item.itemId,
                availableUnits: units,
                unit: defaultUnit || null,
                unitId: defaultUnit?.unitId || null,
                unitPrice,
                isPriceReadOnly,
                itemPolicy: policy,
              }
            : line
        )
      );
    } catch (error) {
      console.error('Failed to load item policy:', error);
    }
  };

  const handleUnitChange = async (lineId: string, unit: ItemUnit | null) => {
    if (!unit) {
      updateLine(lineId, 'unit', null);
      updateLine(lineId, 'unitId', null);
      return;
    }

    const line = detailLines.find(l => l.id === lineId);
    if (!line || !line.itemId) {
      updateLine(lineId, 'unit', unit);
      updateLine(lineId, 'unitId', unit.unitId);
      return;
    }

    let finalPrice = 0;

    // Try to get supplier price first
    if (formData.supplierId) {
      try {
        const priceInfo = await itemSuppliersService.getPrice(
          line.itemId,
          formData.supplierId,
          unit.unitId
        );
        if (priceInfo) {
          finalPrice = priceInfo.price;
        }
      } catch (error) {
        // No supplier price
      }
    }

    // If no supplier price, use default purchase price from ItemUnits
    if (finalPrice === 0 && line.availableUnits) {
      const itemUnit = line.availableUnits.find(u => u.unitId === unit.unitId);
      if (itemUnit) {
        finalPrice = itemUnit.defaultPurchasePrice || 0;
      }
    }

    updateLine(lineId, 'unit', unit);
    updateLine(lineId, 'unitId', unit.unitId);
    updateLine(lineId, 'unitPrice', finalPrice);
  };

  const updateLine = (id: string, field: keyof DetailLine, value: any) => {
    setDetailLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, [field]: value } : line))
    );
  };

  const calculateLineTotal = (line: DetailLine): number => {
    const lineAmount = line.quantity * line.unitPrice;
    const discountAmount = lineAmount * (line.discountPercent / 100);
    const amountAfterDiscount = lineAmount - discountAmount;
    const taxAmount = amountAfterDiscount * (line.taxPercent / 100);
    return amountAfterDiscount + taxAmount;
  };

  const calculateTotals = () => {
    const subTotal = detailLines.reduce((sum, line) => {
      const lineAmount = line.quantity * line.unitPrice;
      const discountAmount = lineAmount * (line.discountPercent / 100);
      const amountAfterDiscount = lineAmount - discountAmount;
      const taxAmount = amountAfterDiscount * (line.taxPercent / 100);
      return sum + (amountAfterDiscount + taxAmount);
    }, 0);

    const headerDiscountAmount = subTotal * (formData.discountPercent / 100);
    const amountAfterHeaderDiscount = subTotal - headerDiscountAmount;
    const headerTaxAmount = amountAfterHeaderDiscount * (formData.taxPercent / 100);
    const totalAmount = amountAfterHeaderDiscount + headerTaxAmount + formData.shippingCost;
    const remainingAmount = totalAmount - formData.paidAmount;

    return {
      subTotal,
      discountAmount: headerDiscountAmount,
      taxAmount: headerTaxAmount,
      totalAmount,
      remainingAmount,
    };
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Either Supplier OR Custody OR PaymentAccount is required
    if (!formData.supplierId && !formData.custodyId && !formData.paymentAccountId) {
      newErrors.supplierId = language === 'ar' ? 'يجب اختيار المورد أو العهدة أو حساب الدفع' : 'Supplier, Custody or Payment Account is required';
      newErrors.custodyId = language === 'ar' ? 'يجب اختيار المورد أو العهدة أو حساب الدفع' : 'Supplier, Custody or Payment Account is required';
      newErrors.paymentAccountId = language === 'ar' ? 'يجب اختيار المورد أو العهدة أو حساب الدفع' : 'Supplier, Custody or Payment Account is required';
    }
    
    if (!formData.warehouseId) newErrors.warehouseId = 'Required';
    
    // Validate dates (Super Admin can use any date)
    if (!isUserSuperAdmin) {
      if (formData.invoiceDate) {
        const invoiceDate = new Date(formData.invoiceDate);
        invoiceDate.setHours(0, 0, 0, 0);
        if (invoiceDate < today) {
          newErrors.invoiceDate = language === 'ar' ? 'يجب أن يكون التاريخ اليوم أو في المستقبل' : 'Date must be today or later';
        }
      }

      if (formData.dueDate) {
        const dueDate = new Date(formData.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        if (dueDate < today) {
          newErrors.dueDate = language === 'ar' ? 'يجب أن يكون التاريخ اليوم أو في المستقبل' : 'Date must be today or later';
        }
      }
    }
    
    if (detailLines.length === 0) newErrors.details = 'Required';

    detailLines.forEach((line, index) => {
      if (!line.itemId) newErrors[`item_${index}`] = 'Required';
      if (!line.unitId) newErrors[`unit_${index}`] = 'Required';
      if (line.quantity <= 0) newErrors[`quantity_${index}`] = 'Must be > 0';
      
      // Track Expiry Date Validation
      if (line.item?.hasExpiryDate) {
        if (!line.batchNumber || line.batchNumber.trim() === '') {
          newErrors[`batchNumber_${index}`] = language === 'ar' 
            ? 'رقم الدفعة مطلوب لهذا الصنف' 
            : 'Batch number required for this item';
        }
        if (!line.expiryDate || line.expiryDate.trim() === '') {
          newErrors[`expiryDate_${index}`] = language === 'ar' 
            ? 'تاريخ الانتهاء مطلوب لهذا الصنف' 
            : 'Expiry date required for this item';
        } else {
          // Validate expiry date is in the future
          const expiryDate = new Date(line.expiryDate);
          expiryDate.setHours(0, 0, 0, 0);
          if (expiryDate <= today) {
            newErrors[`expiryDate_${index}`] = language === 'ar' 
              ? 'تاريخ الانتهاء يجب أن يكون في المستقبل' 
              : 'Expiry date must be in the future';
          }
        }
      }
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const messages: string[] = [];
      if (newErrors.paymentSource) messages.push(newErrors.paymentSource);
      if (newErrors.warehouseId) messages.push(language === 'ar' ? 'المخزن مطلوب' : 'Warehouse is required');
      if (newErrors.invoiceDate) messages.push(newErrors.invoiceDate);
      if (newErrors.dueDate) messages.push(newErrors.dueDate);
      if (newErrors.details) messages.push(language === 'ar' ? 'يجب إضافة صنف واحد على الأقل' : 'At least one item is required');
      const lineErrors = Object.keys(newErrors).filter(k => k.startsWith('item_') || k.startsWith('unit_') || k.startsWith('quantity_') || k.startsWith('batchNumber_') || k.startsWith('expiryDate_'));
      if (lineErrors.length > 0) {
        const uniqueMessages = Array.from(new Set(lineErrors.map(k => newErrors[k])));
        uniqueMessages.forEach(m => messages.push(m));
      }
      toast.error(messages.join(' | '), { autoClose: 5000 });
    }
    return Object.keys(newErrors).length === 0;
  };

  // Validate current step before moving to next
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (step === 0) {
      // Step 0: Invoice Details
      // Either Supplier OR Custody OR PaymentAccount is required
      if (!formData.supplierId && !formData.custodyId && !formData.paymentAccountId) {
        newErrors.supplierId = language === 'ar' ? 'يجب اختيار المورد أو العهدة أو حساب الدفع' : 'Supplier, Custody or Payment Account is required';
        newErrors.custodyId = language === 'ar' ? 'يجب اختيار المورد أو العهدة أو حساب الدفع' : 'Supplier, Custody or Payment Account is required';
        newErrors.paymentAccountId = language === 'ar' ? 'يجب اختيار المورد أو العهدة أو حساب الدفع' : 'Supplier, Custody or Payment Account is required';
      }
      
      if (!formData.warehouseId) newErrors.warehouseId = language === 'ar' ? 'المخزن مطلوب' : 'Warehouse is required';
      
      if (!isUserSuperAdmin) {
        if (formData.invoiceDate) {
          const invoiceDate = new Date(formData.invoiceDate);
          invoiceDate.setHours(0, 0, 0, 0);
          if (invoiceDate < today) {
            newErrors.invoiceDate = language === 'ar' ? 'يجب أن يكون التاريخ اليوم أو في المستقبل' : 'Date must be today or later';
          }
        }

        if (formData.dueDate) {
          const dueDate = new Date(formData.dueDate);
          dueDate.setHours(0, 0, 0, 0);
          if (dueDate < today) {
            newErrors.dueDate = language === 'ar' ? 'يجب أن يكون التاريخ اليوم أو في المستقبل' : 'Date must be today or later';
          }
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
        return false;
      }
    } else if (step === 1) {
      // Step 1: Items
      if (detailLines.length === 0) {
        toast.error(language === 'ar' ? 'يجب إضافة صنف واحد على الأقل' : 'Please add at least one item');
        return false;
      }

      detailLines.forEach((line, index) => {
        if (!line.itemId) newErrors[`item_${index}`] = language === 'ar' ? 'الصنف مطلوب' : 'Item is required';
        if (!line.unitId) newErrors[`unit_${index}`] = language === 'ar' ? 'الوحدة مطلوبة' : 'Unit is required';
        if (line.quantity <= 0) newErrors[`quantity_${index}`] = language === 'ar' ? 'الكمية يجب أن تكون أكبر من 0' : 'Quantity must be greater than 0';
        
        if (line.item?.hasExpiryDate) {
          if (!line.batchNumber || line.batchNumber.trim() === '') {
            newErrors[`batchNumber_${index}`] = language === 'ar' ? 'رقم الدفعة مطلوب لهذا الصنف' : 'Batch number required for this item';
          }
          if (!line.expiryDate || line.expiryDate.trim() === '') {
            newErrors[`expiryDate_${index}`] = language === 'ar' ? 'تاريخ الانتهاء مطلوب لهذا الصنف' : 'Expiry date required for this item';
          } else {
            const expiryDate = new Date(line.expiryDate);
            expiryDate.setHours(0, 0, 0, 0);
            if (expiryDate <= today) {
              newErrors[`expiryDate_${index}`] = language === 'ar' ? 'تاريخ الانتهاء يجب أن يكون في المستقبل' : 'Expiry date must be in the future';
            }
          }
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error(language === 'ar' ? 'يرجى ملء جميع بيانات الأصناف بشكل صحيح' : 'Please fill all item details correctly');
        return false;
      }
    }

    setErrors({});
    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => Math.min(steps.length - 1, prev + 1));
    }
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(0, prev - 1));
    setErrors({}); // Clear errors when going back
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    // Guard against saving with an empty invoice number — happens when the
    // initial auto-generate API call silently fails (logged to console only)
    // and the user clicks Save before noticing the field is blank. Without
    // this guard the backend used to insert an empty number and break the
    // numbering generator for everyone else.
    if (!purchaseInvoice && !formData.invoiceNumber?.trim()) {
      toast.error(isRTL
        ? 'رقم الفاتورة فارغ — أعد فتح النموذج لتوليد رقم جديد.'
        : 'Invoice number is empty — close and reopen the form to generate a new number.');
      await loadNewInvoiceNumber();
      return;
    }

    setLoading(true);
    try {
      if (purchaseInvoice) {
        const updateDto: UpdatePurchaseInvoiceDto = {
          purchaseInvoiceId: purchaseInvoice.purchaseInvoiceId,
          invoiceDate: formData.invoiceDate,
          supplierId: formData.supplierId || undefined,
          warehouseId: formData.warehouseId!,
          custodyId: formData.custodyId || undefined,
          paymentAccountId: formData.paymentAccountId || undefined,
          supplierInvoiceNumber: formData.supplierInvoiceNumber || undefined,
          paymentType: formData.paymentType || undefined,
          dueDate: formData.dueDate || undefined,
          discountPercent: formData.discountPercent,
          taxPercent: formData.taxPercent,
          shippingCost: formData.shippingCost,
          paidAmount: formData.paidAmount,
          status: purchaseInvoice.status,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => {
            let detailId = 0;
            
            if (typeof line.id === 'string') {
              if (line.id.startsWith('temp-')) {
                detailId = 0; // New item
              } else {
                const parsed = Number(line.id);
                // Validate Int32 range: -2,147,483,648 to 2,147,483,647
                detailId = (parsed > 0 && parsed <= 2147483647) ? parsed : 0;
              }
            } else {
              detailId = Number(line.id) || 0;
            }
            
            return {
              invoiceDetailId: detailId,
              lineNumber: index + 1,
              itemId: line.itemId!,
              unitId: line.unitId!,
              quantity: line.quantity,
              unitPrice: line.unitPrice,
              discountPercent: line.discountPercent,
              taxPercent: line.taxPercent,
              batchNumber: line.batchNumber || undefined,
              expiryDate: line.expiryDate || undefined,
              notes: line.notes || undefined,
            };
          }),
        };
        await purchaseInvoicesService.update(purchaseInvoice.purchaseInvoiceId, updateDto);
      } else {
        const createDto: CreatePurchaseInvoiceDto = {
          invoiceNumber: formData.invoiceNumber,
          invoiceDate: formData.invoiceDate,
          supplierId: formData.supplierId || undefined,
          warehouseId: formData.warehouseId!,
          custodyId: formData.custodyId || undefined,
          paymentAccountId: formData.paymentAccountId || undefined,
          supplierInvoiceNumber: formData.supplierInvoiceNumber || undefined,
          paymentType: formData.paymentType || undefined,
          dueDate: formData.dueDate || undefined,
          discountPercent: formData.discountPercent,
          taxPercent: formData.taxPercent,
          shippingCost: formData.shippingCost,
          paidAmount: formData.paidAmount,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => ({
            lineNumber: index + 1,
            itemId: line.itemId!,
            unitId: line.unitId!,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            discountPercent: line.discountPercent,
            taxPercent: line.taxPercent,
            batchNumber: line.batchNumber || undefined,
            expiryDate: line.expiryDate || undefined,
            notes: line.notes || undefined,
          })),
        };
        await purchaseInvoicesService.create(createDto);
      }
      toast.success(t.invoiceSaved);
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Failed to save:', error);
      toast.error(error.response?.data?.message || t.invoiceSaveFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      supplierId: null,
      warehouseId: null,
      custodyId: null,
      paymentAccountId: null,
      supplierInvoiceNumber: '',
      paymentType: 'Cash',
      dueDate: '',
      discountPercent: 0,
      taxPercent: 0,
      shippingCost: 0,
      paidAmount: 0,
      notes: '',
    });
    setDetailLines([]);
    setSelectedSupplier(null);
    setSelectedWarehouse(null);
    setSelectedCustody(null);
    setSelectedPaymentAccount(null);
    setSupplierItems([]);
    setErrors({});
    setActiveStep(0);
    onClose();
  };

  const totals = calculateTotals();
  const paymentTypes = ['Cash', 'Credit', 'Custody'];

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            {/* Header Card */}
            <Card 
              elevation={6}
              sx={{ 
                mb: 3,
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(145deg, #1e3a5f 0%, #2c5282 100%)'
                  : 'linear-gradient(145deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 200,
                  height: 200,
                  background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                  borderRadius: '50%',
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <ReceiptIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {formData.invoiceNumber || '---'}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {t.invoiceNumber}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
                  gap: 2 
                }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label={t.invoiceDate}
                      value={formData.invoiceDate ? new Date(formData.invoiceDate) : null}
                      onChange={(date) => setFormData({ ...formData, invoiceDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                      format="dd/MM/yyyy"
                      views={['year', 'month', 'day']}
                      openTo="day"
                      minDate={minDate}
                      maxDate={maxDate}
                      disabled={dateLocked}
                      slotProps={{ textField: { size: 'small', fullWidth: true, error: !!errors.invoiceDate, helperText: errors.invoiceDate, sx: { '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.15)', '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.9)' }, '& .MuiInputBase-input': { color: 'white' } } } }}
                    />
                  </LocalizationProvider>

                  <TextField
                    fullWidth
                    label={t.supplierInvoice}
                    value={formData.supplierInvoiceNumber}
                    onChange={(e) => setFormData({ ...formData, supplierInvoiceNumber: e.target.value })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.15)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.9)' },
                      '& .MuiInputBase-input': { color: 'white' },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Main Info */}
            <Card elevation={4} sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
                  gap: 3 
                }}>
                  <Autocomplete
                    options={suppliers}
                    getOptionLabel={(option: Supplier) => `${option.supplierCode} - ${isRTL ? option.supplierNameAr : option.supplierName}`}
                    value={selectedSupplier}
                    disabled={!isSuperAdmin && !!formData.custodyId}
                    onChange={(_, newValue) => {
                      setSelectedSupplier(newValue);
                      setFormData({ ...formData, supplierId: newValue?.supplierId || null, paymentType: !isSuperAdmin && newValue ? 'Credit' : formData.paymentType });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t.supplier}
                        required
                        error={!!errors.supplierId}
                        helperText={errors.supplierId}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <BusinessIcon color="primary" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  {fixedWarehouseId ? (
                    <TextField
                      label={t.warehouse}
                      value={selectedWarehouse ? `${selectedWarehouse.warehouseCode} - ${isRTL ? selectedWarehouse.warehouseNameAr : selectedWarehouse.warehouseName}` : ''}
                      disabled
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WarehouseIcon color="secondary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  ) : (
                  <Autocomplete
                    options={warehouses}
                    getOptionLabel={(option: Warehouse) => `${option.warehouseCode} - ${isRTL ? option.warehouseNameAr : option.warehouseName}`}
                    value={selectedWarehouse}
                    onChange={(_, newValue) => {
                      setSelectedWarehouse(newValue);
                      setFormData({ ...formData, warehouseId: newValue?.warehouseId || null });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t.warehouse}
                        required
                        error={!!errors.warehouseId}
                        helperText={errors.warehouseId}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <WarehouseIcon color="secondary" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  )}
                  {/* Custody Selector */}
                  <Autocomplete
                    value={selectedCustody}
                    disabled={!isSuperAdmin && !!formData.supplierId}
                    onChange={(_, newValue) => {
                      setSelectedCustody(newValue);
                      setFormData({ ...formData, custodyId: newValue?.custodyId || null, paymentType: !isSuperAdmin && newValue ? 'Custody' : formData.paymentType });
                    }}
                    options={custodies}
                    getOptionLabel={(option: Custody) => {
                      const employeeName = option.employeeName || '';
                      return `${option.custodyNumber} - ${employeeName}`;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t.custody}
                        placeholder={t.selectCustody}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <MoneyIcon sx={{ color: '#ff9800' }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <Box component="li" key={key} {...otherProps}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <Typography variant="body1" fontWeight="bold">
                              {option.custodyNumber}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.employeeName || ''}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }}
                  />

                  {!(isBranchManager || isWarehouseKeeper) && (
                  <Autocomplete
                    value={selectedPaymentAccount}
                    onChange={(_, newValue) => {
                      setSelectedPaymentAccount(newValue);
                      setFormData({ ...formData, paymentAccountId: newValue?.accountId || null });
                    }}
                    options={accounts}
                    getOptionLabel={(option: any) => {
                      return `${option.accountCode} - ${language === 'ar' ? option.accountNameAr : option.accountName}`;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t.paymentAccount}
                        placeholder={t.selectPaymentAccount}
                        error={!!errors.paymentAccountId}
                        helperText={errors.paymentAccountId}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <PaymentIcon sx={{ color: '#4caf50' }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <Box component="li" key={key} {...otherProps}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <Typography variant="body1" fontWeight="bold">
                              {option.accountCode}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {language === 'ar' ? option.accountNameAr : option.accountName}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }}
                  />
                  )}

                  <Autocomplete
                    options={paymentTypes}
                    value={formData.paymentType}
                    onChange={(_, newValue) => {
                      const newPaymentType = newValue || 'Cash';
                      // Custody is only valid for Custody payment type; keep it cleared otherwise
                      setFormData({
                        ...formData,
                        paymentType: newPaymentType,
                        custodyId: newPaymentType === 'Custody' ? formData.custodyId : null,
                      });
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label={t.paymentType}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <PaymentIcon color="info" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label={t.dueDate}
                      value={formData.dueDate ? new Date(formData.dueDate) : null}
                      onChange={(date) => setFormData({ ...formData, dueDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                      format="dd/MM/yyyy"
                      views={['year', 'month', 'day']}
                      openTo="day"
                      disabled={dateLocked}
                      slotProps={{ textField: { size: 'small', fullWidth: true, error: !!errors.dueDate, helperText: errors.dueDate } }}
                    />
                  </LocalizationProvider>
                </Box>

                {formData.supplierId && supplierItems.length > 0 && (
                  <Alert 
                    severity="success" 
                    icon={<CheckCircleIcon />}
                    sx={{ mt: 3 }}
                  >
                    <strong>{supplierItems.length}</strong> {t.itemsAvailable}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Card elevation={4}>
              <CardContent sx={{ p: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <InventoryIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t.items} ({detailLines.length})
                    </Typography>
                  </Stack>
                  <Button 
                    startIcon={<AddIcon />} 
                    onClick={addNewLine} 
                    variant="contained"
                    size="large"
                    sx={{
                      background: 'linear-gradient(45deg, #f093fb 30%, #f5576c 90%)',
                      px: 4,
                    }}
                  >
                    {t.addItem}
                  </Button>
                </Stack>

                <Box ref={gridRef}>
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{
                          bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8',
                          fontWeight: 700,
                          minWidth: 300,
                        }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CategoryIcon fontSize="small" color="primary" />
                            <span>{t.item}</span>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 180 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <ScaleIcon fontSize="small" color="secondary" />
                            <span>{t.unit}</span>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 110 }}>{t.quantity}</TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 140 }}>{t.price}</TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 100 }}>{t.discount}</TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 100 }}>{t.tax}</TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 140 }}>{t.total}</TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 130 }}>
                          {language === 'ar' ? 'رقم الدفعة' : 'Batch No.'}
                        </TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', fontWeight: 700, minWidth: 140 }}>
                          {language === 'ar' ? 'تاريخ الانتهاء' : 'Expiry Date'}
                        </TableCell>
                        <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f0f4f8', width: 60 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailLines.map((line, index) => (
                        <TableRow 
                          key={line.id}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              transition: 'all 0.2s',
                            } 
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Autocomplete
                              size="small"
                              options={items}
                              getOptionLabel={(option: Item) => `${option.itemCode} - ${isRTL ? option.itemNameAr : option.itemName}`}
                              value={line.item}
                              onChange={(_, newValue) => handleItemChange(line.id, newValue)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={!!errors[`item_${index}`]}
                                  placeholder={t.selectItem}
                                />
                              )}
                            />
                            {line.itemPolicy && getPolicyLabel(line.itemPolicy) && (
                              <Chip
                                label={getPolicyLabel(line.itemPolicy)}
                                size="small"
                                color={line.itemPolicy.isPriceControlled ? 'warning' : 'info'}
                                icon={line.itemPolicy.isPriceControlled ? <LockIcon fontSize="small" /> : undefined}
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Autocomplete
                              size="small"
                              options={line.availableUnits}
                              getOptionLabel={(option: ItemUnit) => `${option.unitCode} - ${isRTL ? option.unitNameAr : option.unitName}`}
                              value={line.unit}
                              onChange={(_, newValue) => handleUnitChange(line.id, newValue)}
                              disabled={!line.item}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={!!errors[`unit_${index}`]}
                                  placeholder={t.selectUnit}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <TextField
                              size="small"
                              value={line.quantity === 0 ? '' : String(line.quantity)}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                  updateLine(line.id, 'quantity', v === '' || v === '.' ? 0 : parseFloat(v));
                                }
                              }}
                              error={!!errors[`quantity_${index}`]}
                              inputProps={{ inputMode: 'decimal' }}
                              sx={{ minWidth: 90 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            {(() => {
                              const itemUnit = line.availableUnits.find(u => u.unitId === line.unitId);
                              const defaultPrice = itemUnit?.defaultPurchasePrice || 0;
                              const isPriceHigher = defaultPrice > 0 && line.unitPrice > defaultPrice;

                              return (
                                <TextField
                                  size="small"
                                  value={line.unitPrice === 0 ? '' : String(line.unitPrice)}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                      updateLine(line.id, 'unitPrice', v === '' || v === '.' ? 0 : parseFloat(v));
                                    }
                                  }}
                                  disabled={line.isPriceReadOnly}
                                  inputProps={{ inputMode: 'decimal' }}
                                  helperText={
                                    isPriceHigher
                                      ? (language === 'ar'
                                          ? `⚠️ السعر الأساسي: ${defaultPrice.toFixed(3)} د.ك`
                                          : `⚠️ Base price: ${defaultPrice.toFixed(3)} KD`)
                                      : ''
                                  }
                                  InputProps={{
                                    endAdornment: line.isPriceReadOnly && (
                                      <InputAdornment position="end">
                                        <Tooltip title={t.priceFromSupplier}>
                                          <LockIcon color="warning" fontSize="small" />
                                        </Tooltip>
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    minWidth: 110,
                                    '& .Mui-disabled': {
                                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                                      color: theme.palette.text.primary,
                                      WebkitTextFillColor: theme.palette.text.primary,
                                    },
                                    '& .MuiFormHelperText-root': {
                                      color: 'warning.main',
                                      fontSize: '0.7rem',
                                    }
                                  }}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={line.discountPercent}
                              onChange={(e) => updateLine(line.id, 'discountPercent', parseFloat(e.target.value) || 0)}
                              inputProps={{ min: 0, step: 0.001 }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                              sx={{ minWidth: 90 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={line.taxPercent}
                              onChange={(e) => updateLine(line.id, 'taxPercent', parseFloat(e.target.value) || 0)}
                              inputProps={{ min: 0, step: 0.001 }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                              sx={{ minWidth: 90 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip 
                              label={`${calculateLineTotal(line).toFixed(3)} KD`}
                              color="success"
                              size="medium"
                              sx={{ fontWeight: 700, minWidth: 120 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <TextField
                              size="small"
                              value={line.batchNumber}
                              onChange={(e) => updateLine(line.id, 'batchNumber', e.target.value)}
                              placeholder={language === 'ar' ? 'رقم الدفعة' : 'Batch #'}
                              required={line.item?.hasExpiryDate}
                              error={!!errors[`batchNumber_${detailLines.indexOf(line)}`]}
                              helperText={errors[`batchNumber_${detailLines.indexOf(line)}`]}
                              sx={{ 
                                minWidth: 120,
                                '& .MuiInputBase-input': {
                                  bgcolor: line.item?.hasExpiryDate ? alpha(theme.palette.warning.main, 0.05) : 'transparent'
                                }
                              }}
                              InputProps={{
                                startAdornment: line.item?.hasExpiryDate ? (
                                  <InputAdornment position="start">
                                    <Typography color="error" sx={{ fontWeight: 'bold' }}>*</Typography>
                                  </InputAdornment>
                                ) : null
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                value={line.expiryDate ? new Date(line.expiryDate) : null}
                                onChange={(date) => updateLine(line.id, 'expiryDate', date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                                format="dd/MM/yyyy"
                                views={['year', 'month', 'day']}
                                openTo="day"
                                slotProps={{ textField: { size: 'small', fullWidth: true, required: line.item?.hasExpiryDate, error: !!errors[`expiryDate_${detailLines.indexOf(line)}`], helperText: errors[`expiryDate_${detailLines.indexOf(line)}`], sx: { minWidth: 130 } } }}
                              />
                            </LocalizationProvider>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => removeLine(line.id)}
                              sx={{
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  transition: 'transform 0.2s',
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 3 }}>
            {/* Summary Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
              <Card sx={{ 
                background: 'linear-gradient(145deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}>
                <CardContent>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.subtotal}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {totals.subTotal.toFixed(3)} <small style={{ fontSize: '0.5em' }}>KD</small>
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ 
                background: 'linear-gradient(145deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
              }}>
                <CardContent>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.grandTotal}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {totals.totalAmount.toFixed(3)} <small style={{ fontSize: '0.5em' }}>KD</small>
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ 
                background: 'linear-gradient(145deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
              }}>
                <CardContent>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.balance}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {totals.remainingAmount.toFixed(3)} <small style={{ fontSize: '0.5em' }}>KD</small>
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Details */}
            <Card elevation={4}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                  <TextField
                    fullWidth
                    label={t.headerDiscount}
                    type="number"
                    size="medium"
                    value={formData.discountPercent}
                    onChange={(e) => setFormData({ ...formData, discountPercent: parseFloat(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: 0.001 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">📉</InputAdornment>,
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />

                  <TextField
                    fullWidth
                    label={t.headerTax}
                    type="number"
                    size="medium"
                    value={formData.taxPercent}
                    onChange={(e) => setFormData({ ...formData, taxPercent: parseFloat(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: 0.001 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">📊</InputAdornment>,
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />

                  <TextField
                    fullWidth
                    label={t.shipping}
                    type="number"
                    size="medium"
                    value={formData.shippingCost}
                    onChange={(e) => setFormData({ ...formData, shippingCost: parseFloat(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: 0.001 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ShippingIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: <InputAdornment position="end">KD</InputAdornment>,
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  size="medium"
                  label={t.paidAmount}
                  type="number"
                  value={formData.paidAmount}
                  onChange={(e) => setFormData({ ...formData, paidAmount: parseFloat(e.target.value) || 0 })}
                  inputProps={{ min: 0, step: 0.001 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MoneyIcon color="success" />
                      </InputAdornment>
                    ),
                    endAdornment: <InputAdornment position="end">KD</InputAdornment>,
                  }}
                  sx={{ mb: 3 }}
                />

                <Divider sx={{ my: 3 }} />

                {/* Breakdown */}
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>{t.subtotal}:</Typography>
                    <Typography fontWeight="600">{totals.subTotal.toFixed(3)} KD</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error">- {t.discount}:</Typography>
                    <Typography color="error" fontWeight="600">{totals.discountAmount.toFixed(3)} KD</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>+ {t.tax}:</Typography>
                    <Typography fontWeight="600">{totals.taxAmount.toFixed(3)} KD</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>+ {t.shipping}:</Typography>
                    <Typography fontWeight="600">{formData.shippingCost.toFixed(3)} KD</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    p: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    borderRadius: 2,
                  }}>
                    <Typography variant="h6">{t.grandTotal}:</Typography>
                    <Typography variant="h5" color="success.main" fontWeight="700">
                      {totals.totalAmount.toFixed(3)} KD
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>{t.paid}:</Typography>
                    <Typography color="primary.main" fontWeight="600">{formData.paidAmount.toFixed(3)} KD</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>{t.balance}:</Typography>
                    <Typography color="error.main" fontWeight="700" variant="h6">{totals.remainingAmount.toFixed(3)} KD</Typography>
                  </Box>
                </Stack>

                <TextField
                  fullWidth
                  label={t.notes}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  multiline
                  rows={3}
                  sx={{ mt: 3 }}
                />
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="xl" 
      fullWidth 
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          minHeight: '95vh',
          maxHeight: '95vh',
          background: theme.palette.mode === 'dark' 
            ? '#0f172a'
            : '#f8fafc',
        }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        p: 3,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <ReceiptIcon sx={{ fontSize: 50 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {t.title}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {purchaseInvoice ? 'Edit' : 'New'}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Progress Bar */}
        <LinearProgress 
          variant="determinate" 
          value={((activeStep + 1) / steps.length) * 100}
          sx={{ 
            mt: 2, 
            height: 6, 
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#fbbf24',
            }
          }}
        />
      </Box>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 3, bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : 'white' }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel 
                StepIconProps={{
                  sx: {
                    '&.Mui-active': {
                      color: '#667eea',
                    },
                    '&.Mui-completed': {
                      color: '#10b981',
                    }
                  }
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: activeStep === index ? 700 : 400,
                    color: activeStep === index ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Content */}
      <DialogContent sx={{ px: 3, py: 2, overflow: 'auto' }}>
        <Container maxWidth="xl" disableGutters>
          {renderStepContent()}
        </Container>
      </DialogContent>

      {/* Actions */}
      <Box sx={{ 
        p: 3, 
        borderTop: 1, 
        borderColor: 'divider',
        bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : 'white',
      }}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            size="large"
            variant="outlined"
            sx={{ minWidth: 120 }}
          >
            {isRTL ? 'السابق' : 'Back'}
          </Button>

          <Stack direction="row" spacing={2}>
            <Button 
              onClick={handleClose} 
              size="large" 
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              {t.cancel}
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                size="large"
                sx={{
                  minWidth: 200,
                  background: 'linear-gradient(45deg, #10b981 30%, #059669 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #059669 30%, #047857 90%)',
                  }
                }}
              >
                {t.save}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                variant="contained"
                size="large"
                sx={{
                  minWidth: 120,
                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                }}
              >
                {isRTL ? 'التالي' : 'Next'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default PurchaseInvoiceDialog;